import authService, { TOKEN_TYPES, IMPERSONATION_KEYS } from "./authService";
import type {
  ImpersonatableRole,
  ImpersonatableUser,
  ImpersonateResponse,
  RoleImpersonateResponse,
} from "../contexts/superuser.types";

export const SUPERUSER_TOKEN_KEY = IMPERSONATION_KEYS.SUPERUSER_TOKEN;
// Persists the impersonated user across a page refresh so the impersonation
// session can be restored instead of silently dropped.
export const IMPERSONATED_USER_KEY = IMPERSONATION_KEYS.IMPERSONATED_USER;

const IAM_URL = import.meta.env.VITE_IAM_URL;

export const superuserService = {
  fetchImpersonatableUsers: async (): Promise<ImpersonatableUser[]> => {
    // Use the saved superuser token if we're currently impersonating,
    // so the /auth/users request is always authorised as the superuser.
    const token =
      sessionStorage.getItem(SUPERUSER_TOKEN_KEY) ??
      authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(`${IAM_URL}/auth/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`${response.status}`);
    const data = await response.json();
    return data.users;
  },

  impersonateUser: async (userId: string): Promise<ImpersonateResponse> => {
    const currentToken = authService.getToken(TOKEN_TYPES.ACCESS);
    if (!currentToken) throw new Error("No active session");

    const response = await fetch(`${IAM_URL}/auth/impersonate/${userId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${currentToken}` },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: string }).error || "Impersonation failed",
      );
    }

    return response.json();
  },

  // --- Role impersonation -------------------------------------------------
  // Unlike user impersonation, the superuser's identity is unchanged, so the
  // normal access token (not a stashed superuser token) is always the right
  // credential and token refresh stays enabled throughout.

  fetchImpersonatableRoles: async (): Promise<ImpersonatableRole[]> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(`${IAM_URL}/auth/impersonatable-roles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`${response.status}`);
    const data = await response.json();
    return data.roles;
  },

  impersonateRole: async (
    roleSlug: string,
  ): Promise<RoleImpersonateResponse> => {
    const currentToken = authService.getToken(TOKEN_TYPES.ACCESS);
    if (!currentToken) throw new Error("No active session");

    const response = await fetch(
      `${IAM_URL}/auth/impersonate-role/${roleSlug}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: string }).error || "Role impersonation failed",
      );
    }

    return response.json();
  },

  endRoleImpersonation: async (): Promise<RoleImpersonateResponse> => {
    const currentToken = authService.getToken(TOKEN_TYPES.ACCESS);
    if (!currentToken) throw new Error("No active session");

    const response = await fetch(`${IAM_URL}/auth/impersonate-role`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${currentToken}` },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: string }).error || "Failed to end role impersonation",
      );
    }

    return response.json();
  },
};
